import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ReferralProgram } from '@/types/referral';
import type { Json } from '@/integrations/supabase/types';

const REFERRAL_PROGRAMS_QUERY_KEY = 'referral-programs';

async function fetchPrograms(brandId: string): Promise<ReferralProgram[]> {
  const { data, error } = await supabase
    .from('os_referral_programs')
    .select(`
      *,
      members:os_referral_program_members(count)
    `)
    .eq('brand_id', brandId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map((p) => ({
    ...p,
    member_count: (p.members as { count?: number }[] | undefined)?.[0]?.count ?? 0,
    members: undefined,
  })) as unknown as ReferralProgram[];
}

export const useReferralPrograms = (brandId: string | null) => {
  const queryClient = useQueryClient();

  const {
    data: programs = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REFERRAL_PROGRAMS_QUERY_KEY, brandId],
    queryFn: () => fetchPrograms(brandId!),
    enabled: !!brandId,
  });

  const createProgram = async (programData: Partial<ReferralProgram>): Promise<ReferralProgram | { error: string } | null> => {
    if (!brandId) return { error: 'No brand selected' };
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { error: 'Not authenticated' };
      const name = programData.name || '';
      const slug = (programData.slug ?? name).toString().toLowerCase().trim().replace(/\s+/g, '-') || 'program';
      const { data, error } = await supabase
        .from('os_referral_programs')
        .insert([{
          brand_id: brandId,
          user_id: user.id,
          name,
          slug,
          type: programData.type || 'generic',
          description: programData.description ?? null,
          settings: (programData.settings ?? {}) as Json,
        }])
        .select()
        .single();
      if (error) return { error: error.message };
      const newProgram = { ...data, member_count: 0 } as ReferralProgram;
      queryClient.setQueryData<ReferralProgram[]>(
        [REFERRAL_PROGRAMS_QUERY_KEY, brandId],
        (prev) => (prev ? [newProgram, ...prev] : [newProgram])
      );
      return newProgram;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create program';
      console.error('Error creating program:', error);
      return { error: message };
    }
  };

  const updateProgram = async (id: string, updates: Partial<ReferralProgram>) => {
    if (!brandId) return null;
    try {
      const { settings, ...rest } = updates;
      const dbUpdates = {
        ...rest,
        ...(settings !== undefined && { settings: settings as Json }),
      };
      const { data, error } = await supabase
        .from('os_referral_programs')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      const prev = queryClient.getQueryData<ReferralProgram[]>([REFERRAL_PROGRAMS_QUERY_KEY, brandId]);
      const current = prev?.find((p) => p.id === id);
      const updated = { ...data, member_count: current?.member_count ?? 0 } as ReferralProgram;
      queryClient.setQueryData<ReferralProgram[]>(
        [REFERRAL_PROGRAMS_QUERY_KEY, brandId],
        (old) => old?.map((p) => (p.id === id ? updated : p)) ?? [updated]
      );
      return updated;
    } catch (error) {
      console.error('Error updating program:', error);
      return null;
    }
  };

  const deleteProgram = async (id: string) => {
    if (!brandId) return false;
    try {
      const { error } = await supabase.from('os_referral_programs').delete().eq('id', id);
      if (error) throw error;
      queryClient.setQueryData<ReferralProgram[]>(
        [REFERRAL_PROGRAMS_QUERY_KEY, brandId],
        (prev) => prev?.filter((p) => p.id !== id) ?? []
      );
      return true;
    } catch (error) {
      console.error('Error deleting program:', error);
      return false;
    }
  };

  return {
    programs,
    isLoading,
    refetch,
    createProgram,
    updateProgram,
    deleteProgram,
  };
};
